import React from "react";
import { CheckCircle2, Award, FileBadge, ClipboardCheck, Download } from "lucide-react";

const GMP_PDF = "/certificados/certificado-gmp.pdf";
const FDA_PDF = "/certificados/registro-fda-bta.pdf";

const bullets = [
  "Validación de instalaciones y procedimientos con auditorías periódicas GMP",
  "Control de calidad en cada lote: materias primas, proceso y producto terminado",
  "Trazabilidad completa desde la materia prima hasta el envasado final",
  "Selección rigurosa de materias primas y proveedores homologados",
  "Reducción activa del desperdicio en fabricación, como parte de nuestro compromiso con la sostenibilidad",
];

export default function Quality() {
  return (
    <section id="calidad" className="relative py-10 md:py-14 bg-white overflow-hidden">
      <div className="absolute inset-0 grid-lines-light opacity-40 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-stretch">
          <div className="flex flex-col">
            <div className="text-xs uppercase tracking-[0.3em] text-[#0A84E2] font-semibold mb-4">/ 03 · Calidad y certificaciones</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0A1628] uppercase tracking-tighter leading-[1]">
              Calidad
              <br />
              <span className="text-[#0A84E2]">y excelencia.</span>
            </h2>
            <p className="mt-6 text-slate-600 text-lg">Mantenemos los estándares de calidad, seguridad alimentaria y sostenibilidad al más alto nivel, con estrictos procesos de validación de instalaciones y procedimientos, para garantizar la máxima excelencia en el producto acabado.</p>

            <ul className="mt-10 space-y-4 flex-1 flex flex-col justify-center">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-[#0A84E2] flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Certifications */}
          <div className="flex flex-col gap-4">
            <a href={GMP_PDF} target="_blank" rel="noopener noreferrer" data-testid="cert-gmp" className="group corp-card rounded-sm p-7 flex-1 flex items-center relative cursor-pointer transition-all duration-300 hover:border-[#0A84E2]/60 hover:shadow-lg hover:-translate-y-0.5">
              <Download className="w-5 h-5 text-slate-300 group-hover:text-[#0A84E2] absolute top-5 right-5 transition-colors" />
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-sm bg-[#EFF6FF] text-[#0A84E2] flex items-center justify-center flex-shrink-0 transition-colors group-hover:bg-[#0A84E2] group-hover:text-white">
                  <Award className="w-6 h-6" />
                </div>
                <div>
                  <div className="font-display text-2xl font-extrabold text-[#0A1628]">Certificado GMP</div>
                  <p className="mt-2 text-sm text-slate-600">Fabricación auditada conforme a <span className="font-semibold text-[#0A1628]">CFR-2011 Title 21 Vol. 2 Part 111</span> y <span className="font-semibold text-[#0A1628]">EHPM Quality Guide</span> (20.10.2022).</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#0A84E2] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Download className="w-3.5 h-3.5" /> Descargar certificado (PDF)
                  </span>
                </div>
              </div>
            </a>

            <a href={FDA_PDF} target="_blank" rel="noopener noreferrer" data-testid="cert-fda" className="group corp-card rounded-sm p-7 flex-1 flex items-center relative cursor-pointer transition-all duration-300 hover:border-[#0A84E2]/60 hover:shadow-lg hover:-translate-y-0.5">
              <Download className="w-5 h-5 text-slate-300 group-hover:text-[#0A84E2] absolute top-5 right-5 transition-colors" />
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-sm bg-[#EFF6FF] text-[#0A84E2] flex items-center justify-center flex-shrink-0 transition-colors group-hover:bg-[#0A84E2] group-hover:text-white">
                  <FileBadge className="w-6 h-6" />
                </div>
                <div>
                  <div className="font-display text-2xl font-extrabold text-[#0A1628]">Registro FDA (EE.UU.)</div>
                  <p className="mt-2 text-sm text-slate-600">Instalación registrada ante la FDA — <span className="font-semibold text-[#0A1628]">nº 17816438408</span>. Capacidad de comercializar nuestros productos en el mercado norteamericano.</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#0A84E2] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Download className="w-3.5 h-3.5" /> Descargar certificado (PDF)
                  </span>
                </div>
              </div>
            </a>

            <div data-testid="cert-appcc" className="corp-card rounded-sm p-7 flex-1 flex items-center">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-sm bg-[#EFF6FF] text-[#0A84E2] flex items-center justify-center flex-shrink-0">
                  <ClipboardCheck className="w-6 h-6" />
                </div>
                <div>
                  <div className="font-display text-2xl font-extrabold text-[#0A1628]">Sistema de autocontrol APPCC</div>
                  <p className="mt-2 text-sm text-slate-600">Programa de autocontrol documentado, con planes de higiene, trazabilidad y control de proveedores.</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {[
                { code: "GMP", label: "Buenas prácticas", href: GMP_PDF },
                { code: "FDA", label: "Registro EE.UU.", href: FDA_PDF },
                { code: "APPCC", label: "Autocontrol", href: null },
              ].map((c, i) =>
                c.href ? (
                  <a
                    key={c.code}
                    href={c.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`cert-badge-${i}`}
                    title={`Descargar certificado ${c.code} (PDF)`}
                    className="group corp-card rounded-sm p-5 text-center relative cursor-pointer transition-all duration-300 hover:border-[#0A84E2]/60 hover:shadow-md hover:-translate-y-0.5"
                  >
                    <Download className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#0A84E2] absolute top-2 right-2 transition-colors" />
                    <div className="font-display text-xl md:text-2xl font-extrabold text-[#0A1628]">{c.code}</div>
                    <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold group-hover:text-[#0A84E2] transition-colors">Descargar PDF</div>
                  </a>
                ) : (
                  <div key={c.code} data-testid={`cert-badge-${i}`} className="corp-card rounded-sm p-5 text-center">
                    <div className="font-display text-xl md:text-2xl font-extrabold text-[#0A1628]">{c.code}</div>
                    <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{c.label}</div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
